import React, { useRef, useEffect } from "react";
import {
  FormTab,
  TextInput,
  required,
  Record,
  TabbedForm,
  SelectInput,
  BooleanInput,
  FormDataConsumer,
  useRecordContext,
} from "react-admin";
import { useForm } from "react-final-form";
import { useAppData } from "../../../common/hooks/useAppData";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { DeviceType, Vendor } from "../../../common/data/service-proxies";
import {
  geometrisDeviceTypes,
  iosixDeviceTypes,
  pacificTrackDeviceTypes,
} from "../../../static/eldDeviceTypes";

interface EldFormProps {
  record?: Record;
}

const VendorSelectInput = () => {
  const form = useForm();

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <SelectInput
          {...rest}
          source="vendorType"
          label="resources.master-elds.labels.vendor"
          choices={[
            { id: Vendor.Geometris, name: "Geometris" },
            { id: Vendor.PacificTrack, name: "Pacific Track" },
            { id: Vendor.Iosix, name: "iOSIX" },
          ]}
          validate={required()}
          variant="outlined"
          onChange={(event) => {
            form.change("deviceType", undefined);
            const newVendor = event?.target?.value;
            if (newVendor === Vendor.PacificTrack) {
              form.change("debuggingEnabled", false);
              form.change("dataPlanEnabled", false);
            } else if (newVendor === Vendor.Geometris) {
              form.change("macAddress", undefined);
              form.change("deviceName", undefined);
            } else if (newVendor === Vendor.Iosix) {
              form.change("serialNumber", undefined);
              form.change("deviceName", undefined);
              form.change("deviceType", DeviceType.Io1020);
              form.change("debuggingEnabled", false);
              form.change("dataPlanEnabled", false);
            }
          }}
        />
      )}
    </FormDataConsumer>
  );
};

const DeviceTypeSelectInput = () => {
  const form = useForm();

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => {
        const vendorType = formData?.vendorType;

        let deviceTypeOptions: Array<{
          id: DeviceType;
          name: string;
        }> = [];
        if (vendorType === Vendor.Geometris) {
          deviceTypeOptions = geometrisDeviceTypes;
        } else if (vendorType === Vendor.PacificTrack) {
          deviceTypeOptions = pacificTrackDeviceTypes;
        } else if (vendorType === Vendor.Iosix) {
          deviceTypeOptions = iosixDeviceTypes;
        }

        return (
          <SelectInput
            {...rest}
            source="deviceType"
            label="resources.master-elds.labels.deviceType"
            choices={deviceTypeOptions}
            validate={required()}
            variant="outlined"
            onChange={(event: any) => {
              const newDeviceType = event?.target?.value;
              if (vendorType === Vendor.PacificTrack) {
                if (newDeviceType !== DeviceType.PacificTrack30) {
                  form.change("macAddress", undefined);
                }
                if (
                  newDeviceType !== DeviceType.PacificTrack40 &&
                  newDeviceType !== DeviceType.PacificTrack30
                ) {
                  form.change("deviceName", undefined);
                }
              }
            }}
          />
        );
      }}
    </FormDataConsumer>
  );
};

export const EldForm = (props: EldFormProps) => {
  const record = useRecordContext(props);
  const isUpdate = Boolean(record?.id);
  const assignedTenantRef = useRef<string>();
  useEffect(() => {
    if (!props.record) {
      return;
    }

    if (props.record?.assignedToTenant) {
      assignedTenantRef.current = props.record.assignedToTenant;
    }

    if (assignedTenantRef.current) {
      props.record.assignedToTenant = assignedTenantRef.current;
    }
  }, [props.record]);

  const tenants = useAppData((x) => x.tenants) ?? [];

  return (
    <TabbedForm className="form-create-edit" variant="outlined" {...props}>
      <FormTab label="resources.master-elds.labels.generalInformation">
        <ProtectedElement source="vendorType">
          <VendorSelectInput />
        </ProtectedElement>
        <ProtectedElement source="deviceType">
          <DeviceTypeSelectInput />
        </ProtectedElement>
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            const vendor = formData?.vendorType;
            const deviceType = formData?.deviceType;

            const showSerialNumber =
              vendor && deviceType && vendor !== Vendor.Iosix;

            if (!showSerialNumber) {
              return null;
            }

            return (
              <ProtectedElement source="serialNumber">
                <TextInput
                  {...rest}
                  source="serialNumber"
                  label="resources.master-elds.labels.serialNumber"
                  validate={required()}
                />
              </ProtectedElement>
            );
          }}
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            const vendor = formData?.vendorType;
            const deviceType = formData?.deviceType;

            if (
              vendor === Vendor.Iosix ||
              (vendor === Vendor.PacificTrack &&
                deviceType === DeviceType.PacificTrack30)
            ) {
              return (
                <>
                  <ProtectedElement source="macAddress">
                    <TextInput
                      source="macAddress"
                      label="resources.master-elds.labels.macAddress"
                      validate={required()}
                      {...rest}
                    />
                  </ProtectedElement>
                  {isUpdate &&
                    vendor === Vendor.PacificTrack &&
                    deviceType === DeviceType.PacificTrack30 && (
                      <ProtectedElement source="deviceName">
                        <TextInput
                          source="deviceName"
                          label="resources.master-elds.labels.deviceName"
                          {...rest}
                        />
                      </ProtectedElement>
                    )}
                </>
              );
            }

            if (
              vendor === Vendor.PacificTrack &&
              deviceType === DeviceType.PacificTrack40
            ) {
              return (
                <ProtectedElement source="deviceName">
                  <TextInput
                    source="deviceName"
                    label="resources.master-elds.labels.deviceName"
                    validate={required()}
                    {...rest}
                  />
                </ProtectedElement>
              );
            }

            return null;
          }}
        </FormDataConsumer>
        <ProtectedElement source="assignedToTenantId">
          <SelectInput
            source="assignedToTenantId"
            label="resources.master-elds.labels.assignedToTenantId"
            choices={tenants}
            allowEmpty
            emptyText="resources.master-elds.labels.noTenant"
          />
        </ProtectedElement>
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            if (formData?.vendorType !== Vendor.Geometris) {
              return null;
            }

            return (
              <>
                <ProtectedElement source="debuggingEnabled">
                  <BooleanInput source="debuggingEnabled" {...rest} />
                </ProtectedElement>
                <ProtectedElement source="dataPlanEnabled">
                  <BooleanInput
                    source="dataPlanEnabled"
                    defaultValue={true}
                    {...rest}
                  />
                </ProtectedElement>
              </>
            );
          }}
        </FormDataConsumer>
        <ProtectedElement source="note" label="Notes">
          <TextInput source="note" multiline />
        </ProtectedElement>
      </FormTab>
    </TabbedForm>
  );
};
